import { useEffect, useState } from "react";
import { showChatNotice } from "./chatNotice";

// Unsent text per chat, kept in this browser so switching chats (or reloading) never
// loses it - Slack-style. One key per staff member, company and chat, so a shared
// computer never shows one person's draft to another. Storage can be full, blocked
// or missing (private windows) - then drafts just last for this page load.
const PREFIX = "chat-draft";
const MAX_DRAFT_CHARS = 4000;
const memory = new Map();
const listeners = new Set();
// Storage refused a draft: said once per page load, not on every keystroke
let warnedStorageFailed = false;

const keyOf = (scope, conversationId) => `${PREFIX}:${scope}:${conversationId}`;

const emit = () => listeners.forEach((listener) => listener());

export const loadDraft = (scope, conversationId) => {
  if (!scope || !conversationId) return "";
  const key = keyOf(scope, conversationId);
  if (memory.has(key)) return memory.get(key);
  let value = "";
  try {
    value = window.localStorage.getItem(key) || "";
  } catch {
    // Storage unavailable - nothing saved before
  }
  memory.set(key, value);
  return value;
};

export const saveDraft = (scope, conversationId, text) => {
  if (!scope || !conversationId) return;
  const key = keyOf(scope, conversationId);
  const value = (text || "").slice(0, MAX_DRAFT_CHARS);
  // Whitespace alone isn't a draft
  const kept = value.trim() ? value : "";
  if ((memory.get(key) ?? null) === kept) return;
  memory.set(key, kept);
  try {
    if (kept) window.localStorage.setItem(key, kept);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage full or blocked - the in-memory copy still covers this page load
    if (kept && !warnedStorageFailed) {
      warnedStorageFailed = true;
      showChatNotice("Your draft couldn't be saved. It will be lost if you reload or close this tab.");
    }
  }
  emit();
};

// The message a draft is replying to (its id), per chat - for this page load only. The chat
// puts the reply back only while that message is loaded and still there to see, so nothing
// deleted or hidden since comes back from here
const replies = new Map();
export const loadDraftReply = (scope, conversationId) =>
  (scope && conversationId && replies.get(keyOf(scope, conversationId))) || null;
export const saveDraftReply = (scope, conversationId, messageId) => {
  if (!scope || !conversationId) return;
  if (messageId) replies.set(keyOf(scope, conversationId), messageId);
  else replies.delete(keyOf(scope, conversationId));
};

// Logged out: drafts held for this page load go too (the saved ones went with localStorage)
export const forgetDraftMemory = () => {
  memory.clear();
  replies.clear();
  emit();
};

// Re-renders the caller when any draft changes (the list shows "Draft: ...")
export const useChatDrafts = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
};

// staff + company: the same person in another company has other chats
export const draftScope = (staff) => (staff?.id ? `${staff.company || staff.companyId || "c"}:${staff.id}` : null);
